import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Self,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import { NgControl, Validators } from '@angular/forms';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
} from '@ngneat/reactive-forms';

import { DisposalSite } from '@digital-platform/disposal-sites/domain';
import { NestedFormComponent } from '@digital-platform/shared/ui';
import { WasteProfile } from '@digital-platform/waste-profiles/domain';

type DestinationFormElements = Pick<WasteProfile, 'destination'>;
interface DestinationRadioFormElements {
  destination: DestinationFormElements['destination']['id'];
}

interface DestinationFormControls {
  destination: FormControl<DisposalSite>;
}

@Component({
  selector: 'digital-platform-disposal-destination',
  templateUrl: './disposal-destination.component.html',
  styleUrls: ['./disposal-destination.component.scss'],
})
export class DisposalDestinationComponent
  extends NestedFormComponent<DestinationFormElements, DestinationFormControls>
  implements
    OnInit,
    OnDestroy,
    OnChanges,
    ControlValueAccessor<DestinationFormElements>
{
  @Input() public asbestosType: 'FRIABLE' | 'NONFRIABLE' | 'NONE';
  @Input() profileStatus: string;
  @Input() isLegacy: boolean;
  @Input() tabIndex = 1;

  form: FormGroup<DestinationFormControls>;

  constructor(
    @Optional() @Self() public controlDir: NgControl,
    private formBuilder: FormBuilder
  ) {
    super(controlDir);
  }

  initializeForm(disposalDestination: DisposalSite) {
    this.form = this.formBuilder.group({
      destination: !this.isLegacy
        ? [disposalDestination || null, [Validators.required]]
        : [disposalDestination || null],
    });
  }

  updateForm(disposalDestination: DisposalSite) {
    this.form.patchValue(
      { destination: disposalDestination },
      { emitEvent: false }
    );
  }

  ngOnInit(): void {
    let disposalDestination: DisposalSite = null;

    if (this.value) {
      disposalDestination = this.value.destination;
    }

    if (this.form) {
      this.updateForm(disposalDestination);
    } else {
      this.initializeForm(disposalDestination);
    }

    super.ngOnInit();
  }

  hasError(field: keyof DestinationRadioFormElements, error: string): boolean {
    const control = this.form.get(field);
    return (
      control.touched &&
      control.hasError(error) &&
      this.profileStatus !== 'Not Submitted'
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.profileStatus?.currentValue === 'Not Submitted') {
      this.form.controls.destination.removeValidators(Validators.required);
      this.form.controls.destination.updateValueAndValidity();
    } else if (
      !this.isLegacy &&
      changes.profileStatus?.currentValue &&
      changes.profileStatus?.currentValue !== 'Not Submitted'
    ) {
      this.form.controls.destination.addValidators(Validators.required);
    }
  }
}
